/**
 * @file ProductCardsGrid.tsx
 * @description Вид «Плитки» реестра товаров (phase19.5; phase19.31 — макет
 * (а): по переключателю «Таблица / Плитки»).
 *
 * Плитка — ссылка на карточку товара: фото квадратом, название (до двух
 * строк), «категория · цена», пилюля «На сайте» и «⋯» с действиями строки.
 * Сетка mobile-first: 2 в ряд на телефоне (фото ~160px), 3 с 768, 4 с 1024,
 * 5 с 1440. Стикеров с эмодзи и текста мельче 12px нет (SC29, SC43); товар
 * без фото узнаётся по заглушке.
 */

import { Link } from "react-router";

import { ProductThumb } from "./ProductThumb";
import { RowActionsMenu, type RowAction } from "../ui/RowActionsMenu";
import { StatusPill } from "../ui/StatusPill";
import type { CatalogCategory, CatalogProduct } from "../../lib/api-catalog";
import { categoryLabel } from "../../lib/catalog-category-tree";
import { productPriceText } from "../../lib/catalog-format";
import { PRODUCT_SITE_META, productSiteState } from "../../lib/catalog-status";

interface Props {
  products: CatalogProduct[];
  categoriesById: Map<string, CatalogCategory>;
  rowHref: (product: CatalogProduct) => string;
  rowActions: (product: CatalogProduct) => RowAction[];
}

export function ProductCardsGrid({ products, categoriesById, rowHref, rowActions }: Props) {
  return (
    <ul aria-label="Товары плитками" className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 min-[1440px]:grid-cols-5">
      {products.map((p) => {
        const category = categoriesById.get(p.categoryId);
        return (
          <li
            key={p.id}
            className="relative flex min-w-0 flex-col overflow-hidden rounded-xl bg-surface-lowest [box-shadow:inset_0_0_0_1px_var(--border)]"
          >
            <Link
              to={rowHref(p)}
              aria-label={`Товар ${p.displayName}`}
              className="flex min-w-0 flex-1 flex-col outline-none transition-colors hover:bg-[var(--row-hover)] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            >
              <ProductThumb src={p.primaryImageUrl} alt="" className="aspect-square w-full" />
              <span className="flex min-w-0 flex-col gap-1 p-3 pr-12">
                <span className="type-body line-clamp-2 font-medium text-on-surface">{p.displayName}</span>
                <span className="type-caption truncate text-on-surface-variant">
                  {category ? categoryLabel(category) : "Без категории"}
                </span>
                <span className="type-body font-semibold tabular-nums text-on-surface">{productPriceText(p)}</span>
                <span>
                  <StatusPill meta={PRODUCT_SITE_META[productSiteState(p)]} />
                </span>
              </span>
            </Link>
            {/* «⋯» поверх нижнего правого угла: кнопка не вложена в ссылку. */}
            <div className="absolute bottom-1 right-1">
              <RowActionsMenu actions={rowActions(p)} size="compact" title={`Действия: ${p.displayName}`} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
